import { useIsLibraryUploadQualityChecksEnabled } from '../../flags';

/**
 * The OCR/text-extraction quality outcomes that warrant surfacing a tag.
 * `'ok'` documents intentionally have no tag, so it is excluded here.
 */
export type DocumentOcrQualityFlag = 'warning' | 'error';

/**
 * EVI-1625: map a backend OCR-quality `label` to the subset of outcomes that
 * render a badge. `ok` (clean extraction) and any absent / unknown value
 * (web/partner sources, documents uploaded before the feature shipped, backfill
 * pending) resolve to `undefined` so the FE shows no badge — we never fabricate
 * a quality signal the backend didn't send.
 */
export const resolveOcrQualityFlag = (
  label?: string | null
): DocumentOcrQualityFlag | undefined =>
  label === 'warning' || label === 'error' ? label : undefined;

/**
 * EVI-1625: resolve the text-extraction quality badge for a cited library
 * document from the backend-provided `text_extraction_quality` field on the
 * citation. Shared by the source popover card and the references list so both
 * stay in lockstep for the same citation.
 *
 * Returns `undefined` when the quality-checks feature flag is off, no quality
 * was provided, or the document extracted cleanly (`ok`).
 */
export const useCitationOcrQuality = (
  quality?: string | null
): DocumentOcrQualityFlag | undefined => {
  const isQualityChecksEnabled = useIsLibraryUploadQualityChecksEnabled();

  if (!isQualityChecksEnabled) return undefined;

  return resolveOcrQualityFlag(quality);
};

/**
 * EVI-1625: resolve the text-extraction quality badge for a completed library
 * document, read from its backend-persisted `document_metadata.ocr_quality.label`.
 *
 * Delegates to {@link useCitationOcrQuality} so the feature-flag gating lives in
 * a single place. Returns `undefined` when the quality-checks feature flag is
 * off, no quality was recorded (older documents predating the feature), or the
 * document extracted cleanly (`ok`).
 */
export const useLibraryUploadOcrQuality = (
  label?: string | null
): DocumentOcrQualityFlag | undefined => useCitationOcrQuality(label);
